// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faKeyboard,
    faWandMagicSparkles,
    faLanguage,
    faGraduationCap,
    faArrowRight,
    faBookOpen,
    faBook,
    faRotate,
    faChartLine,
    faList,
    faBrain,
    faPenNib,
    faSpellCheck,
    faMicrophone,
    faScroll,
    faQuestion,
    faPrint,
    faPuzzlePiece,
} from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import { useAuth } from "../../context/AuthContext";
import { useContinueLearning } from "../../hooks/useContinueLearning";
import { useUserXp } from "../../hooks/useUserXp";
import { useRecentActivity } from "../../hooks/useRecentActivity";
import { activitySummaryCopy } from "./activitySummaryCopy";
import { useAchievements } from "../../hooks/useAchievements";
import AchievementBadge from "../Achievements/AchievementBadge";
import MissionsCard from "./MissionsCard";
import { hiraganaWritingList, katakanaWritingList } from "../../consts/kanaWritingList";
import { joyoKanji } from "../../consts/joyoKanji";
import { grammarPoints } from "../../consts/grammarPoints";
import { VOCABULARY_TOTAL } from "../../consts/vocabularyTotal";
import { VERB_TOTAL } from "../../consts/verbTotal";
import ProgressRing from "../ProgressRing/ProgressRing";
import styles from "./homePage.module.scss";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { useProgressText } from "../../i18n/progressText";

const startLearningItems = [
    { to: "/lessons", icon: faGraduationCap, itemKey: "lessons", hasBadge: true },
    { to: "/kana-writing-practice?script=katakana", icon: faPenNib, itemKey: "kanaWriting" },
    { to: "/grammar", icon: faSpellCheck, itemKey: "grammar" },
    { to: "/kanji-writing-practice", kana: "漢", itemKey: "kanjiWriting" },
    { to: "/kanji-roulette", icon: faRotate, itemKey: "kanjiRoulette" },
    { to: "/kanji-memory-quiz", icon: faBrain, itemKey: "kanjiQuiz" },
    { to: "/kanji-quiz", icon: faQuestion, itemKey: "kanjiGuessQuiz" },
    { to: "/vocabulary", icon: faBook, itemKey: "vocabulary" },
    { to: "/conjugation", icon: faRotate, itemKey: "conjugation" },
    { to: "/translate", icon: faLanguage, itemKey: "translate" },
    { to: "/speaking-practice", icon: faMicrophone, itemKey: "speaking" },
];

const integrationItems = [
    { to: "/wanikani", icon: faChartLine, itemKey: "wanikani" },
];

const toolItems = [
    { to: "/numbers", icon: faLanguage, itemKey: "numbers" },
    { to: "/keyboard", icon: faKeyboard, itemKey: "keyboard" },
    { to: "/converter", icon: faWandMagicSparkles, itemKey: "converter" },
    { to: "/romaji-converter", icon: faLanguage, itemKey: "romajiConverter" },
    { to: "/worksheets", icon: faPrint, itemKey: "worksheets", isNew: true },
];

const referenceItems = [
    { to: "/study-materials", icon: faBookOpen, itemKey: "studyMaterials" },
    { to: "/blog/joyo-kanji-list", icon: faScroll, itemKey: "joyoKanjiList" },
    { to: "/blog/full-kanji-list", icon: faList, itemKey: "fullKanjiList" },
    { to: "/radicals", icon: faPuzzlePiece, itemKey: "radicals" },
    { to: "/blog", icon: faBookOpen, itemKey: "blog" },
    { to: "/jlpt", icon: faGraduationCap, itemKey: "jlpt", isNew: true },
];

function PathCard({ item }) {
    const lp = useLocalizedPath();
    const { t } = useTranslation("homePage");
    const base = `startLearning.items.${item.itemKey}`;

    return (
        <Link to={lp(item.to)} className={styles.pathCard}>
            {item.hasBadge && <span className={styles.pathCardBadge}>{t(`${base}.badge`)}</span>}
            <div className={styles.pathCardIconWrapper}>
                {item.kana ? (
                    <span className={styles.pathCardKanaIcon} aria-hidden="true">{item.kana}</span>
                ) : (
                    <FontAwesomeIcon icon={item.icon} className={styles.pathCardIcon} />
                )}
            </div>
            <div className={styles.pathCardBody}>
                <h3 className={styles.pathCardTitle}>{t(`${base}.title`)}</h3>
                <p className={styles.pathCardText}>{t(`${base}.description`)}</p>
                <span className={styles.pathCardCta}>
                    {t(`${base}.cta`)}
                    <FontAwesomeIcon icon={faArrowRight} className={styles.pathCardCtaIcon} />
                </span>
            </div>
        </Link>
    );
}

function ToolCard({ item, section }) {
    const lp = useLocalizedPath();
    const { t } = useTranslation("homePage");
    const base = `${section}.items.${item.itemKey}`;
    const accessibleLabel = item.isNew ? t(`${base}.accessibleLabel`) : undefined;

    return (
        <Link
            to={lp(item.to)}
            className={`${styles.toolCard} ${item.isNew ? styles.toolCardNew : ""}`}
            aria-label={item.isNew ? `${accessibleLabel} - new feature` : accessibleLabel}
        >
            <div className={styles.pathCardIconWrapper}>
                <FontAwesomeIcon icon={item.icon} className={styles.pathCardIcon} />
            </div>
            <span className={styles.toolCardText}>
                <span className={styles.toolCardTitleRow}>
                    <span className={styles.toolCardTitle}>{t(`${base}.title`)}</span>
                    {item.isNew && <span className={styles.toolCardNewBadge}>{t("reference.newBadge")}</span>}
                </span>
                <span className={styles.toolCardDescription}>{t(`${base}.description`)}</span>
            </span>
        </Link>
    );
}

function ContinueOptionLink({ item }) {
    const lp = useLocalizedPath();
    const { t } = useTranslation("homePage");
    return (
        <Link to={lp(item.to)} className={styles.continueSecondaryLink}>
            {item.percent !== undefined && (
                <ProgressRing percent={item.percent} label={`${item.title} progress`} />
            )}
            <span className={styles.continueSecondaryText}>
                <span className={styles.continueSecondaryTitle}>{item.title}</span>
                <span className={styles.continueSecondaryDetail}>{item.detail}</span>
            </span>
            <span className={styles.continueSecondaryCta}>
                <span className={styles.continueSecondaryCtaLabel}>{t("continueLearning.continue")}</span>
                <FontAwesomeIcon icon={faArrowRight} className={styles.pathCardCtaIcon} />
            </span>
        </Link>
    );
}

function ContinueLearningSection({ authStatus, isSigningOut, isReady, recommendation, activity }) {
    const lp = useLocalizedPath();
    const { t, i18n } = useTranslation("homePage");
    const location = useLocation();
    const activityLoading = authStatus === "authenticated" && activity?.status === "loading";

    if (!isReady || isSigningOut || (activityLoading && recommendation)) {
        return (
            <section className={styles.continueSection} aria-label={t("aria.continueLearning")}>
                <div className={styles.continueSpinnerCard} role="status">
                    <span className={styles.continueSpinner} aria-hidden="true" />
                    <span className={styles.srOnly}>
                        {isSigningOut ? t("continueLearning.signingOut") : t("continueLearning.loading")}
                    </span>
                </div>
            </section>
        );
    }

    if (!recommendation) return null;

    const { primary, secondary } = recommendation;

    if (authStatus !== "authenticated") {
        return (
            <section className={styles.continueSection} aria-labelledby="continue-learning-heading">
                <div className={styles.continueCard}>
                    <div className={styles.continueCardMain}>
                        <p className={styles.continueEyebrow}>{primary.detail}</p>
                        <h2 id="continue-learning-heading" className={styles.continueHeading}>
                            {t("continueLearning.loginHeading")}
                        </h2>
                        <p className={styles.continueDetail}>
                            {t("continueLearning.loginBody")}
                        </p>
                        <Link to={lp("/login")} state={{ from: location }} className={styles.continueCta}>
                            {t("continueLearning.logIn")}
                            <FontAwesomeIcon icon={faArrowRight} className={styles.pathCardCtaIcon} />
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    const summary = activity?.summary ?? null;
    const copy = summary ? activitySummaryCopy(summary, t, i18n.language) : null;
    const keepUnfinishedSession = primary.priority === 1;
    const cta = summary && !keepUnfinishedSession
        ? { to: summary.primary.to, title: t(`activitySummary.titles.${summary.primary.id}`) }
        : { to: primary.to, title: primary.title };
    const otherOptions = copy ? secondary.filter((item) => item.to !== cta.to) : secondary;

    return (
        <section className={styles.continueSection} aria-labelledby="continue-learning-heading">
            <div className={styles.continueCard}>
                <div className={copy ? `${styles.continueCardMain} ${styles.continueCardMainWithSummary}` : styles.continueCardMain}>
                    <p className={styles.continueEyebrow}>{t("continueLearning.welcomeBack")}</p>
                    <h2 id="continue-learning-heading" className={styles.continueHeading}>
                        {copy ? copy.heading : t("continueLearning.continueWith", { title: primary.title })}
                    </h2>
                    <p className={styles.continueDetail}>{copy ? copy.detail : primary.detail}</p>
                    <Link to={lp(cta.to)} className={styles.continueCta}>
                        {copy ? t("continueLearning.continueWith", { title: cta.title }) : t("continueLearning.continue")}
                        <FontAwesomeIcon icon={faArrowRight} className={styles.pathCardCtaIcon} />
                    </Link>
                </div>
                {otherOptions.length > 0 && (
                    <ul className={styles.continueSecondaryList}>
                        {otherOptions.map((item) => (
                            <li key={item.key}>
                                <ContinueOptionLink item={item} />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </section>
    );
}

function LearningStatsSection({ visible }) {
    const lp = useLocalizedPath();
    const { xpLabel, xpUnit } = useProgressText();
    const { t } = useTranslation("homePage");
    const { status, progress, breakdown, hasProgress, refresh } = useUserXp();
    const { unlocked: unlockedAchievements } = useAchievements();
    const highlights = breakdown.entries
        .filter((entry) => entry.xp > 0)
        .sort((a, b) => b.xp - a.xp)
        .slice(0, 3);

    if (!visible) return null;

    return (
        <section className={styles.learningStats} aria-labelledby="learning-stats-heading">
            <div className={styles.learningStatsIntro}>
                <p className={styles.learningStatsEyebrow}>{t("learningStats.eyebrow")}</p>
                <h2 id="learning-stats-heading" className={styles.learningStatsTitle}>{t("learningStats.title")}</h2>
                <Link to={lp("/profile")} className={styles.learningStatsLink}>{t("learningStats.viewProfile")}</Link>
            </div>
            {!hasProgress && status === "loading" && (
                <p className={styles.learningStatsLoading} role="status">{t("learningStats.loading")}</p>
            )}
            {!hasProgress && status === "error" && (
                <p className={styles.learningStatsError} role="alert">
                    {t("learningStats.error")} <button type="button" onClick={refresh}>{t("learningStats.tryAgain")}</button>
                </p>
            )}
            {hasProgress && (
                <div className={styles.learningStatsContent}>
                    <div className={styles.learningStatsLevel}>
                        <strong>{t("learningStats.level", { level: progress.level })}</strong>
                        <span>{t("learningStats.xp", { xp: progress.xp })}</span>
                        <progress aria-label={t("aria.progressToNextLevel")} value={progress.xpIntoLevel} max={progress.xpForNextLevel} />
                        <small>
                            {t("learningStats.xpToNext", {
                                remaining: progress.nextLevelXp - progress.xp,
                                next: progress.level + 1,
                            })}
                        </small>
                        {unlockedAchievements.length > 0 && (
                            <div className={styles.learningStatsAchievements} aria-label={t("learningStats.achievementsEarned")}>
                                {unlockedAchievements.map((achievement) => (
                                    <AchievementBadge key={achievement.id} achievement={achievement} size={30} />
                                ))}
                            </div>
                        )}
                    </div>
                    <ul className={styles.learningStatsBreakdown} aria-label={t("learningStats.topStudyAreas")}>
                        {highlights.map((entry) => (
                            <li key={entry.feature}>
                                <span>{xpLabel(entry)}</span>
                                <span className={styles.learningStatsEntryValue}>
                                    <strong>{t("learningStats.xp", { xp: entry.xp })}</strong>
                                    {entry.unit && (
                                        <small>{entry.count} {xpUnit(entry)}</small>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

function ScriptProgressBar({
    kanaDrawCounts,
    kanjiProgress,
    grammarProgress = {},
    vocabularyProgress = {},
    conjugationProgress = {},
}) {
    const { t } = useTranslation("homePage");
    const completedKana = (characters) => characters.filter(({ symbol }) => (kanaDrawCounts[symbol] || 0) > 0).length;
    const completionPercent = (completed, total) => Math.round((completed / total) * 100);
    const knownKanji = joyoKanji.filter((symbol) => kanjiProgress[symbol] === "known").length;
    const learnedGrammar = grammarPoints.filter(({ id }) => grammarProgress[id] === "learned").length;
    const learnedVocabulary = Object.values(vocabularyProgress).filter((value) => value === "learned").length;
    const learnedConjugations = Object.values(conjugationProgress).filter((value) => value === "learned").length;
    const scripts = [
        { label: t("scriptLabels.hiragana"), percent: completionPercent(completedKana(hiraganaWritingList), hiraganaWritingList.length) },
        { label: t("scriptLabels.katakana"), percent: completionPercent(completedKana(katakanaWritingList), katakanaWritingList.length) },
        { label: t("scriptLabels.kanji"), percent: completionPercent(knownKanji, joyoKanji.length) },
        { label: t("scriptLabels.grammar"), percent: completionPercent(learnedGrammar, grammarPoints.length) },
        { label: t("scriptLabels.vocabulary"), percent: completionPercent(learnedVocabulary, VOCABULARY_TOTAL) },
        { label: t("scriptLabels.conjugation"), percent: completionPercent(learnedConjugations, VERB_TOTAL) },
    ];

    return (
        <div className={styles.scriptProgress} role="group" aria-label={t("aria.scriptProgress")}>
            <div className={styles.scriptProgressTrack}>
                {scripts.map((script) => (
                    <div key={script.label} className={styles.scriptProgressZone} aria-label={`${script.label} completion: ${script.percent}%`}>
                        <span className={styles.scriptProgressFill} style={{ width: `${script.percent}%` }} aria-hidden="true" />
                        <span className={styles.scriptProgressLabel}>{script.label}</span>
                        <strong className={styles.scriptProgressPercent}>{script.percent}%</strong>
                    </div>
                ))}
            </div>
        </div>
    );
}

function HomePage() {
    const lp = useLocalizedPath();
    const { t, i18n } = useTranslation("homePage");
    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt" : "/";
    const location = useLocation();
    const { status: authStatus, isSigningOut } = useAuth();
    const continueLearning = useContinueLearning();
    const recentActivity = useRecentActivity();
    const showHero =
        authStatus === "unauthenticated" ||
        (authStatus === "authenticated" && continueLearning.isReady && !continueLearning.recommendation);
    // Missions share the welcome-back board's audience and its loading gates
    // (every progress source resolved, and the board's own activity spinner
    // gone), so they never flash partial progress or sit under a spinner that
    // is about to grow. Guests get the board's log-in prompt instead -
    // missions and their achievements need an account.
    const showMissions =
        authStatus === "authenticated" &&
        !isSigningOut &&
        continueLearning.isReady &&
        Boolean(continueLearning.recommendation) &&
        recentActivity?.status !== "loading";
    // Any failed progress source makes the missions' numbers untrustworthy, so
    // the card says so rather than showing zeros for work the learner did.
    const missionsLoadFailed = [
        continueLearning.kanaDrawSyncStatus,
        continueLearning.kanjiDrawSyncStatus,
        continueLearning.kanaListenSyncStatus,
        continueLearning.kanjiListenSyncStatus,
    ].includes("error");

    useEffect(() => {
        if (!location.hash) return;
        const target = document.getElementById(location.hash.slice(1));
        if (!target) return;
        const prefersReducedMotion =
            typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        target.scrollIntoView({ behavior: prefersReducedMotion ? "auto" : "smooth", block: "start" });
    }, [location.hash]);

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
                suffixBrand={false}
            />
            <Header />

            <main className={styles.main}>
                {showHero && (
                    <section className={styles.hero} aria-labelledby="hero-heading">
                        <span className={styles.heroEyebrow} aria-hidden="true">
                            日本語学習プラットフォーム
                        </span>
                        <h1 id="hero-heading" className={styles.heroTitle}>
                            {t("hero.title")}
                        </h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero.subtitle")}
                        </p>
                        <div className={styles.heroActions}>
                            <Link to={lp("/lessons")} className={styles.heroPrimaryButton}>
                                {t("hero.startLearning")}
                            </Link>
                            <Link to={lp("/keyboard")} className={styles.heroSecondaryButton}>
                                {t("hero.openKeyboard")}
                            </Link>
                        </div>
                    </section>
                )}


                <LearningStatsSection
                    visible={authStatus === "authenticated" && continueLearning.isReady && Boolean(continueLearning.recommendation)}
                />

                <ContinueLearningSection
                    authStatus={authStatus}
                    isSigningOut={isSigningOut}
                    {...continueLearning}
                    activity={recentActivity}
                />

                {showMissions && (
                    <MissionsCard
                        snapshots={{
                            kanaDrawCounts: continueLearning.kanaDrawCounts,
                            kanjiDrawCounts: continueLearning.kanjiDrawCounts,
                            kanaListenCounts: continueLearning.kanaListenCounts,
                            kanjiListenCounts: continueLearning.kanjiListenCounts,
                        }}
                        loadFailed={missionsLoadFailed}
                    />
                )}

                <section className={styles.pathSection} id="start-learning" aria-labelledby="start-learning-heading">
                    <div className={styles.pathSectionHeader}>
                        <h2 id="start-learning-heading" className={styles.pathSectionHeading}>
                            {t("startLearning.heading")}
                        </h2>
                        <p className={styles.pathSectionDescription}>
                            {t("startLearning.description")}
                        </p>
                    </div>
                    {authStatus === "authenticated" && continueLearning.isReady && Boolean(continueLearning.recommendation) && (
                        <ScriptProgressBar
                            kanaDrawCounts={continueLearning.kanaDrawCounts}
                            kanjiProgress={continueLearning.kanjiProgress}
                            grammarProgress={continueLearning.grammarProgress}
                            vocabularyProgress={continueLearning.vocabularyProgress}
                            conjugationProgress={continueLearning.conjugationProgress}
                        />
                    )}
                    <div className={styles.pathGridPrimary}>
                        {startLearningItems.map((item) => (
                            <PathCard key={item.to} item={item} />
                        ))}
                    </div>
                </section>

                <section className={styles.toolSection} aria-labelledby="integrations-heading">
                    <div className={styles.pathSectionHeader}>
                        <h2 id="integrations-heading" className={styles.pathSectionHeading}>
                            {t("integrations.heading")}
                        </h2>
                        <p className={styles.pathSectionDescription}>
                            {t("integrations.description")}
                        </p>
                    </div>
                    <div className={styles.toolGrid}>
                        {integrationItems.map((item) => (
                            <ToolCard key={item.to} item={item} section="integrations" />
                        ))}
                    </div>
                </section>

                <section className={styles.toolSection} aria-labelledby="tools-heading">
                    <p><Link to={lp("/offline")}>{i18n.language === "pt" ? "Transferências para estudar offline" : "Offline study downloads"}</Link></p>
                    <div className={styles.pathSectionHeader}>
                        <h2 id="tools-heading" className={styles.pathSectionHeading}>
                            {t("tools.heading")}
                        </h2>
                        <p className={styles.pathSectionDescription}>
                            {t("tools.description")}
                        </p>
                    </div>
                    <div className={styles.toolGrid}>
                        {toolItems.map((item) => (
                            <ToolCard key={item.to} item={item} section="tools" />
                        ))}
                    </div>

                    <div className={styles.toolGrid}>
                        {referenceItems.map((item) => (
                            <ToolCard key={item.to} item={item} section="reference" />
                        ))}
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}

export default HomePage;
